// Stale-tab recovery after a deploy. Two halves:
//
//   1. fetchDeployedVersion() — reads /version.json (emitted by the Vite build, served no-cache
//      by nginx). AppUpdateWatcher polls it and compares against the running bundle's
//      __APP_VERSION__ to learn a new deploy landed.
//   2. installChunkReloadGuard() — the reactive net for the window the poller can't cover: a
//      deploy replaces the hashed chunk files, so a tab still on the old index.html 404s the
//      moment it lazy-loads a route it hasn't visited yet. Vite surfaces that as a
//      `vite:preloadError` event; one guarded reload picks up the new index.html, which names
//      chunks that exist again.

/** Reads the deployed version beacon. Null on any failure — offline, mid-deploy 404, bad shape —
 * because a failed check must never look like "update available". */
export async function fetchDeployedVersion(): Promise<string | null> {
  try {
    // `no-store` bypasses the browser cache outright; the nginx `no-cache` header is the
    // second belt for anything else in the path (the file is ~30 bytes, so no 304 needed).
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    const version = (data as { version?: unknown } | null)?.version;
    return typeof version === 'string' ? version : null;
  } catch {
    return null;
  }
}

/** One reload attempt per bundle version per tab session. The key includes the version so the
 * post-reload bundle (new version) gets its own fresh attempt, while a reload that did NOT fix
 * the 404 (server actually broken) can't loop — the second event falls through to the route
 * error boundary instead. Returns false when storage is unavailable: no guard means no reload. */
export function shouldAttemptChunkReload(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  runningVersion: string
): boolean {
  const key = `csc-wms:chunk-reload-attempted:${runningVersion}`;
  try {
    if (storage.getItem(key) !== null) return false;
    storage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
}

/** Call once at boot (main.tsx), before the router mounts. PROD-only: in dev Vite serves modules
 * individually and a preload error is a real bug to surface, not a stale deploy to paper over. */
export function installChunkReloadGuard(): void {
  if (!import.meta.env.PROD) return;
  window.addEventListener('vite:preloadError', (event) => {
    if (!shouldAttemptChunkReload(window.sessionStorage, __APP_VERSION__)) return;
    // preventDefault stops Vite from also throwing the import error into the tree we are
    // about to tear down anyway.
    event.preventDefault();
    window.location.reload();
  });
}
